import { getColor } from '@/utils/theme/colors';
import { Box, Button, Drawer } from '@mui/material';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { FilterTileData } from './filters-tile';
import FlexBox from '@/atomic-components/FlexBox';
import { getStyle } from '@/utils/theme/fonts';
import { useEffect, useState } from 'react';
import CheckedBox from '@/assets/checked-box.svg';
import UncheckedBox from '@/assets/unchecked-box.svg';
import CrossIcon from '@/assets/minty-cross.svg';
import DrawerClose from '@/assets/drawer-close.svg';
import LencoButton from '@/atomic-components/LencoButton';

type FilterDrawerProps = {
	open: boolean;
	onClose: (e: any) => void;
	filters: FilterTileData[];
	preSelectedFilters?: { key: string; val: string }[];
	clearAllFilter: () => void;
	onFilterConfirm: (selectedFilters: { key: string; val: string }[]) => void;
};

function FiltersDrawer(props: FilterDrawerProps) {
	const [selectedSubFilter, setSelectedSubFilter] = useState<FilterTileData>(
		props.filters[0]
	);
	const [selectedFilters, setSelectedFilters] = useState<
		{ key: string; val: string }[]
	>(props.preSelectedFilters ? props.preSelectedFilters : []);
	useEffect(() => {
		setSelectedFilters(
			props.preSelectedFilters ? props.preSelectedFilters : []
		);
	}, [props.preSelectedFilters]);
	const onFilterSelect = (key: string, val: string) => {
		let filterData;
		if (selectedFilters.find((x: any) => x.val == val)) {
			filterData = selectedFilters.filter((x: any) => {
				return x.val != val;
			});
			setSelectedFilters(filterData);
		} else {
			setSelectedFilters([...selectedFilters, { key: key, val: val }]);
		}
	};
	return (
		<Drawer
			anchor={'right'}
			open={props.open}
			onClose={() => props.onClose(!props.open)}
		>
			<Box sx={{ background: getColor(''), padding: '80px', width: '100%' }}>
				<FlexBox
					sx={{
						justifyContent: 'space-between',
						width: '100%',
						alignItems: 'center',
						paddingBottom: '16px',
						borderBottom: `1px solid ${getColor('Gray -4')}`,
					}}
				>
					<LencoTypography
						sx={{ ...getStyle('H9A_Medium'), color: getColor('Gray 0') }}
					>
						Filter by
					</LencoTypography>
					<Box
						onClick={() => props.onClose(!props.open)}
						sx={{ cursor: 'pointer' }}
					>
						<DrawerClose />
					</Box>
				</FlexBox>
				<FlexBox
					sx={{
						justifyContent: 'space-between',
						width: '100%',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{
							maxHeight: '450px',
							minHeight: '500px',
							overflow: 'scroll',
							'::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						{props?.filters?.map((filter) => {
							return (
								<Box
									key={filter.value}
									sx={{
										width: '100%',
										borderBottom: `1px solid ${getColor('Gray -5')}`,
										color:
											selectedSubFilter.label == filter.label
												? getColor('Minty 0')
												: getColor('Gray -1'),
										padding: '24px 130px 24px 0',
										background:
											selectedSubFilter.label == filter.label
												? getColor('Minty -6')
												: getColor(''),
										borderRight:
											selectedSubFilter.label == filter.label
												? `4px solid ${getColor('Minty -3')}`
												: 'none',
										':hover': {
											transition: 'all 0.5s ease',
											paddingLeft: '16px',
											background: getColor('Minty -6'),
											color: getColor('Minty 0'),
										},
									}}
									onClick={() => {
										setSelectedSubFilter(filter);
									}}
								>
									<LencoTypography sx={{ ...getStyle('P_Medium') }}>
										{filter.label}
									</LencoTypography>
								</Box>
							);
						})}
					</Box>
					<Box
						sx={{
							background: getColor('Minty -6'),
							padding: '30px 40px',
							maxHeight: '500px',
							minHeight: '500px',
							overflow: 'scroll',
							'::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						{selectedSubFilter?.options?.map((subFilter) => {
							const isFilterSelected =
								selectedFilters &&
								selectedFilters.find(
									(element: any) => element?.val == subFilter?.label
								);
							return (
								<FlexBox
									key={subFilter.label}
									sx={{
										width: '100%',
										justifyContent: 'space-between',
										borderBottom: `1px solid ${getColor('Minty -3')}`,
										color: getColor('Gray -1'),
										padding: '8px 8px ',
										':hover': {
											color: getColor('Minty 0'),
											background: getColor('Minty -4'),
											transition: '0.2s all linear',
										},
									}}
									onClick={() =>
										onFilterSelect(selectedSubFilter.value, subFilter.label)
									}
								>
									<LencoTypography sx={{ paddingRight: '100px' }}>
										{subFilter.label}
									</LencoTypography>
									{isFilterSelected ? <CheckedBox /> : <UncheckedBox />}
								</FlexBox>
							);
						})}
					</Box>
				</FlexBox>
				<Box
					sx={{
						position: 'absolute',
						bottom: 24,
						left: 0,
						width: '100%',
					}}
				>
					<FlexBox
						width='100%'
						sx={{
							flexDirection: 'row',
							justifyContent: 'center',
							width: '100%',
							alignItems: 'center',
						}}
					>
						<LencoButton
							variant='text'
							onClick={() => {
								props.clearAllFilter();
								props.onClose(!props.open);
								setSelectedFilters([]);
							}}
						>
							{' '}
							<LencoTypography
								sx={{
									textTransform: 'capitalize',
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									cursor: 'pointer',
									...getStyle('Small_Regular'),
									color: getColor('Minty +1'),
								}}
							>
								<CrossIcon /> Clear All Filters
							</LencoTypography>
						</LencoButton>
						<Button
							sx={{
								margin: '16px',
								padding: '14px 60px',
								background: getColor('Minty +1'),
								color: getColor(''),
								borderRadius: '24px',
								':hover': {
									color: getColor('Minty +1'),
									background: getColor(''),
									boxShadow: ' 0px 36px 50px -18px rgba(119,119,119,1);',
									border: `1px solid ${getColor('Gray 0')}`,
								},
							}}
							onClick={() => {
								props.onFilterConfirm(selectedFilters);
								props.onClose(!props.open);
							}}
						>
							Show Results
						</Button>
					</FlexBox>
				</Box>
			</Box>
		</Drawer>
	);
}

export default FiltersDrawer;
